import { Link } from "@tanstack/react-router";
import { buttonVariants } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";

export interface BusinessAllowance {
  canCreate: boolean;
  reason: "max_businesses" | "free_limit" | null;
  paused: boolean;
  owned: number;
  max: number;
  free: number;
  maxFree: number;
  freeBusinesses: { id: string; name: string }[];
}

/** How many more businesses this person may open; always asked fresh, never from cache. */
export const allowanceQuery = {
  queryKey: ["tenants", "allowance"],
  queryFn: () => api.get<BusinessAllowance>("/tenants/allowance"),
  staleTime: 0,
};

/** Why another business can't be opened, and the way forward. */
export function BusinessLimitNotice({
  allowance,
  onNavigate,
}: {
  allowance: BusinessAllowance;
  onNavigate?: () => void;
}) {
  const { t } = useI18n();
  if (allowance.paused) return <p className="text-sm">{t("businessLimit.paused")}</p>;
  if (allowance.reason === "max_businesses")
    return (
      <div className="space-y-3 text-sm">
        <p>{t("businessLimit.max", { max: allowance.max })}</p>
        <p className="text-muted-foreground">{t("businessLimit.maxHelp")}</p>
      </div>
    );
  return (
    <div className="space-y-4 text-sm">
      <p>
        {allowance.maxFree === 0
          ? t("businessLimit.noFree")
          : t("businessLimit.free", { count: allowance.maxFree })}
      </p>
      <p className="text-muted-foreground">{t("businessLimit.freeHelp")}</p>
      <div className="flex flex-col gap-2">
        {allowance.freeBusinesses.map((b) => (
          <Link
            key={b.id}
            to="/t/$tenantId/billing"
            params={{ tenantId: b.id }}
            onClick={onNavigate}
            className={buttonVariants({ variant: "primary" })}
          >
            {t("businessLimit.upgrade", { name: b.name })}
          </Link>
        ))}
      </div>
    </div>
  );
}
